import React from 'react';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import { get } from 'lodash-es';

import { actions as routingActions } from '../../ducks/routing.js';
import { actions as authActions } from '../../ducks/auth.js';
import { actions as appActions } from '../../ducks/app.js';
import { actions as customerActions } from '../../ducks/customer.js';

import DashLayout from '../../components/DashLayout/DashLayout.js';
import AccountSummarySection from '../../components/AccountSummarySection/AccountSummarySection.js';
import AppointmentsSection from '../../components/AppointmentsSection/AppointmentsSection.js';
import MessagesSection from '../../components/MessagesSection/MessagesSection.js';
import BalanceCard from '../../components/AccountsSection/BalanceCard/BalanceCard.js';
import MessageCard from '../../components/MessagesSection/MessageCard/MessageCard.js';
import AppointmentSummaryCard from '../../components/AppointmentsSection/AppointmentSummaryCard/AppointmentSummaryCard';
import withLoader from '../../components/Loader/withLoader.js';
import ReplaceContact from '../../components/Replace/ReplaceContact/ReplaceContact.js';
import CardSpacer from '../../components/CardSpacer/CardSpacer';

import AccountsIcon from '@material-ui/icons/AccountBalanceOutlined';
import MoneyIcon from '@material-ui/icons/AttachMoney';
import MaintenanceIcon from '@material-ui/icons/BuildOutlined';
import OutageIcon from '@material-ui/icons/ReportProblemOutlined';
import MessagesIcon from '@material-ui/icons/NotificationImportantOutlined';

import { getString, stringToDate } from '../../helpers/stringHelpers.js';
import { getCurrentOutage } from '../../helpers/outageHelpers';
import { getLoadedStatus } from '../../helpers/loaderHelpers';
import { hasRole } from '../../helpers/authHelpers.js';
import { isFeatureDisabled } from '../../helpers/outageHelpers';
import constants from '../../constants';
import analyticsApi from '../../api/analytics.js';
import moment from 'moment';
import preferencesAPI from '../../api/preferences';

const stringsComponent = 'dash';
const accountsStringsComponent = 'accounts';

class Dash extends React.Component {
  constructor(props) {
    super(props);

    this.isTermsAccepted = this.isTermsAccepted.bind(this);
  }

  componentDidMount() {
    const { setAppBarTitle } = this.props;

    setAppBarTitle(getString(stringsComponent, 'TITLE'));
  }

  shouldComponentUpdate(nextProps) {
    // Only rerender if not in a unloaded status.
    // This prevents the flash effect as APIs are loaded.
    // Also don't rerender if dashMessageExpanded prop changes
    // as this dissrupts the animation.
    if (
      getLoadedStatus(this.getLoadedStatuses(nextProps)) !== 'UNLOADED' &&
      getLoadedStatus(this.getLoadedStatuses(this.props)) !== 'FAILED_NOT_FOUND'
    ) {
      return true;
    }
    return false;
  }

  getLoadedStatuses(props = this.props) {
    const { tenancy, account, roles, preferences } = props;
    const loaded = [tenancy.loaded, preferences.loaded];

    if (hasRole(roles, constants.ROLE_ACCOUNT_READ)) {
      loaded.push(account.loaded);
    }
    return loaded;
  }

  isTermsAccepted() {
    const { termsAccepted, preferences } = this.props;

    if (termsAccepted) {
      return true;
    } else {
      // Use preferences view indicator to determine whether to show terms and conditions or not
      const termsAlreadyAccepted =
        preferences.data.viewIndicators &&
        preferences.data.viewIndicators.find(
          (vi) =>
            vi.type === 'terms' &&
            vi.version === latestTerms.version &&
            vi.action === 'accepted'
        );

      return termsAlreadyAccepted;
    }
  }

  render() {
    const {
      tenancy,
      account,
      partyRef,
      firstName,
      getAccounts,
      signout,
      authenticated,
      inOutage,
      outageFrom,
      outageTo,
      outageMessage,
      goAccountDetails,
      goAppointments,
      goArrangementDetails,
      goArrangementCreate,
      toggleContactModal,
      dashMessageExpanded,
      toggleDashMessageExpanded,
      preferencesInitialized,
      setPreferencesInitialized,
      roles,
      onLoadComplete,
      customerName,
      preferences,
    } = this.props;

    const tenancyData = tenancy.data;
    const accountData = account.data;

    const rentBalanceAmount =
      tenancyData.hasOwnProperty('currentTenancy') &&
      tenancyData.currentTenancy.hasOwnProperty('rentBalance')
        ? Number(tenancyData.currentTenancy.rentBalance)
        : 0;

    const rentBalance = (
      <BalanceCard
        title={getString(stringsComponent, 'RENT_TITLE')}
        balance={rentBalanceAmount}
        label={
          getString(stringsComponent, 'DATE_LABEL') +
          constants.TEXT_SEPERATOR +
          stringToDate(new Date())
        }
        paymentReference={
          accountData.hasOwnProperty('accounts') &&
          accountData.accounts.hasOwnProperty('rent')
            ? getString('accounts', 'PAYMENT_REFERENCE') +
              constants.TEXT_SEPERATOR +
              accountData.accounts.rent.paymentReference
            : null
        }
        icon={<AccountsIcon />}
        helpText={portalContent.DASH_RENT}
        action={() => goAccountDetails('rent')}
        actionLabel={getString(accountsStringsComponent, 'VIEW_DETAILS_LABEL')}
        actionTwo={
          get(accountData, 'accounts.rent.arrangements[0]', null)
            ? () => goArrangementDetails('rent')
            : null
        }
        actionTwoLabel={getString('accounts', 'VIEW_ARRANGEMENTS_LABEL')}
        gaEvent={() => analyticsApi.event('Help', 'Click', 'Rent Balance')}
        gaEventDismissArrangement={() =>
          analyticsApi.event('Rent Arrangement', 'Click', 'Dismiss')
        }
        showArrangementAction={
          rentBalanceAmount != 0 &&
          !get(accountData, 'accounts.rent.arrangements', false)
        }
        hasMaintenanceArrangement={Boolean(
          get(accountData, 'accounts.maintenance.arrangements', false)
        )}
        arrangementFeatureDisabled={isFeatureDisabled(
          'ARRANGEMENTS_WRITE',
          tenancyData.area
        )}
        arrangementCreateAction={() => goArrangementCreate('rent')}
        toggleContactModal={toggleContactModal}
        customerRoles={roles}
      />
    );

    const lastPayment = (
      <BalanceCard
        title={getString(stringsComponent, 'LAST_PAYMENT_TITLE')}
        balance={
          tenancyData.hasOwnProperty('currentTenancy') &&
          tenancyData.currentTenancy.hasOwnProperty('lastPayment')
            ? Number(tenancyData.currentTenancy.lastPayment)
            : 0
        }
        label={
          tenancyData.hasOwnProperty('currentTenancy') &&
          tenancyData.currentTenancy.hasOwnProperty('lastPaymentDate')
            ? stringToDate(tenancyData.currentTenancy.lastPaymentDate)
            : ''
        }
        icon={<MoneyIcon />}
        helpText={portalContent.DASH_LAST_PAYMENT}
      />
    );

    const repairsBalanceAmount =
      tenancyData.hasOwnProperty('currentTenancy') &&
      tenancyData.currentTenancy.hasOwnProperty('maintenanceBalance')
        ? Number(tenancyData.currentTenancy.maintenanceBalance)
        : 0;

    const repairsBalance = (
      <BalanceCard
        title={getString(stringsComponent, 'REPAIRS_TITLE')}
        balance={repairsBalanceAmount}
        label={
          getString(stringsComponent, 'DATE_LABEL') +
          constants.TEXT_SEPERATOR +
          stringToDate(new Date())
        }
        paymentReference={
          accountData.hasOwnProperty('accounts') &&
          accountData.accounts.hasOwnProperty('maintenance')
            ? getString('accounts', 'PAYMENT_REFERENCE') +
              constants.TEXT_SEPERATOR +
              accountData.accounts.maintenance.paymentReference
            : null
        }
        icon={<MaintenanceIcon />}
        helpText={portalContent.DASH_REPAIRS}
        action={() => goAccountDetails('maintenance')}
        actionLabel={getString(accountsStringsComponent, 'VIEW_DETAILS_LABEL')}
        actionTwo={
          get(accountData, 'accounts.maintenance.arrangements[0]', null)
            ? () => goArrangementDetails('maintenance')
            : null
        }
        actionTwoLabel={getString('accounts', 'VIEW_ARRANGEMENTS_LABEL')}
        gaEvent={() =>
          analyticsApi.event('Help', 'Click', 'Maintenance Balance')
        }
        showArrangementAction={
          repairsBalanceAmount != 0 &&
          !get(accountData, 'accounts.maintenance.arrangements', false)
        }
        isMaintenanceBalance={true}
        hasRentArrangementOrDebt={
          Boolean(get(accountData, 'accounts.rent.arrangements[0]', false)) ||
          rentBalanceAmount < 0
        }
        arrangementFeatureDisabled={isFeatureDisabled(
          'ARRANGEMENTS_WRITE',
          tenancyData.area
        )}
        arrangementCreateAction={() => goArrangementCreate('maintenance')}
        toggleContactModal={toggleContactModal}
        customerRoles={roles}
        gaEventDismissArrangement={() =>
          analyticsApi.event('Maintenance Arrangement', 'Click', 'Dismiss')
        }
      />
    );

    const accountSummary = (
      <AccountSummarySection
        rentBalance={rentBalance}
        lastPayment={lastPayment}
        maintenanceBalance={repairsBalance}
      />
    );

    const currentOutage = getCurrentOutage(outages);

    const messages = [];

    if (currentOutage) {
      messages.push(
        <MessageCard
          title={currentOutage.Title}
          icon={<OutageIcon />}
          type="outage"
          key={`outage-0`}
          body={
            <ReplaceContact
              content={currentOutage.Message}
              action={toggleContactModal}
              isLink={true}
            />
          }
        />
      );
    }

    let displayedMessages = 0;
    portalMessages.forEach((item) => {
      const messageLimit = currentOutage ? 2 : 3;

      const showMessage =
        displayedMessages < messageLimit &&
        (item.Areas.includes(tenancyData.area) || item.Areas.length === 0);
      if (showMessage) {
        displayedMessages++;
      }

      const i = displayedMessages - 1;

      let messageDismissed = false;

      // Use preferences view indicator to determine whether to show banner or not
      if (preferences.loaded === 'LOADED' && !preferencesInitialized) {
        const viewIndicator = preferences.data.viewIndicators
          ? preferences.data.viewIndicators.find(
              (vi) => vi.id === 'banner' + item.ID
            )
          : null;

        messageDismissed = viewIndicator
          ? viewIndicator.action === 'dismissed'
          : false;

        if (messageDismissed && dashMessageExpanded[i]) {
          toggleDashMessageExpanded(i);
        }
      }

      if (showMessage) {
        messages.push(
          <React.Fragment key={`message-${i}`}>
            {(i > 0 || currentOutage) && (
              <CardSpacer key={`message-spacer-${i}`} />
            )}
            <MessageCard
              title={item.Title}
              icon={<MessagesIcon />}
              type="message"
              key={`messageCard-${i}`}
              body={
                <ReplaceContact
                  content={item.Message}
                  action={toggleContactModal}
                  isLink={true}
                />
              }
              isHTML={true}
              initialExpand={dashMessageExpanded[i]}
              additionalAction={() => {
                // If the banner is expanded, collapse it and mark it as dismissed
                // Otherwise expand it and mark it as viewed
                const action = !dashMessageExpanded[i] ? 'viewed' : 'dismissed';

                // Remove milliseconds from datetime string
                const currentDateTime = moment()
                  .toISOString(true)
                  .split('.')[0];

                const viewIndicatorId = 'banner' + item.ID;

                const data = {
                  id: viewIndicatorId,
                  type: 'banner',
                  version: '1',
                  action: action,
                  date: currentDateTime,
                };

                preferencesAPI.createOrUpdateViewIndicator(data);

                toggleDashMessageExpanded(i);
              }}
              collapsable={true}
            />
          </React.Fragment>
        );
      }
    });

    if (preferences.loaded === 'LOADED' && !preferencesInitialized) {
      setPreferencesInitialized();
    }

    const messagesSection = messages.length ? (
      <MessagesSection messages={messages} />
    ) : null;

    let appointmentCards = [];
    tenancyData.hasOwnProperty('currentTenancy') &&
      tenancyData.currentTenancy.hasOwnProperty('appointments') &&
      tenancyData.currentTenancy.appointments.forEach((appointment, i) => {
        if (i === 0) {
          appointmentCards.push(
            <React.Fragment key={`appointment-${i}`}>
              {i > 0 && <CardSpacer key={`appointment-spacer-${i}`} />}
              <AppointmentSummaryCard
                title={appointment.reason}
                description={appointment.reasonDescription}
                date={stringToDate(appointment.date)}
                timeFrom={appointment.start}
                timeTo={appointment.end}
                key={`appointmentCard-${i}`}
                action={() => {
                  goAppointments();
                }}
              />
            </React.Fragment>
          );
        }
      });

    const appointmentsSection = appointmentCards.length ? (
      <AppointmentsSection appointments={appointmentCards} />
    ) : null;

    const WrappedDashLayout = withLoader(DashLayout);
    return (
      <WrappedDashLayout
        accountSummary={
          hasRole(roles, constants.ROLE_ACCOUNT_READ) ? accountSummary : null
        }
        messages={messagesSection}
        appointments={appointmentsSection}
        firstName={firstName}
        authenticated={authenticated}
        onRefresh={getAccounts}
        onError={signout}
        loadText={getString(stringsComponent, 'ACCOUNT_LOAD')}
        loaded={this.getLoadedStatuses()}
        inOutage={inOutage}
        outageFrom={outageFrom}
        outageTo={outageTo}
        outageMessage={
          outageMessage && (
            <ReplaceContact
              content={outageMessage}
              action={toggleContactModal}
              isLink={true}
            />
          )
        }
        failureText={
          <ReplaceContact
            content={portalContent.APP_ERROR_MESSAGE}
            action={toggleContactModal}
            isLink={true}
          />
        }
        notFoundText={
          <ReplaceContact
            content={portalContent.APP_NOT_FOUND_MESSAGE}
            action={toggleContactModal}
            isLink={true}
          />
        }
        toggleContactModal={toggleContactModal}
        onLoadComplete={onLoadComplete}
      />
    );
  }
}

const mapStateToProps = (state) => ({
  tenancy: state.customer.tenancy,
  account: state.customer.account,
  partyRef: state.auth.reference,
  firstName: state.auth.firstName,
  lastName: state.auth.lastName,
  authenticated: state.auth.authenticated,
  inOutage: state.app.inOutage,
  outageFrom: state.app.outageFrom,
  outageTo: state.app.outageTo,
  dashMessageExpanded: state.app.dashMessageExpanded,
  preferencesInitialized: state.app.preferencesInitialized,
  termsAccepted: state.app.termsAccepted,
  outageMessage: state.app.outageMessage,
  customerName: `${state.auth.firstName} ${state.auth.lastName}`,
  roles: state.auth.roles,
  preferences: state.customer.preferences,
});

const mapDispatchToProps = (dispatch) =>
  bindActionCreators(
    {
      ...routingActions,
      ...appActions,
      ...customerActions,
      ...authActions,
    },
    dispatch
  );

export default connect(mapStateToProps, mapDispatchToProps)(Dash);
